// src/components/ChatAssistant.jsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Fab,
  Drawer,
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Avatar,
  Chip,
  useTheme,
  Skeleton,
  Button,
  Stack,
  Badge
} from '@mui/material';
import {
  Chat as ChatIcon,
  Send as SendIcon,
  Close as CloseIcon,
  WhatsApp as WhatsAppIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Facebook as FacebookIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const LEMDA_SYSTEM = {
  role: 'system',
  content: `
You are Lemda Assist — a professional, courteous customer-care chatbot for a leading dev agency.
Keep replies under 40 words, upbeat, respectful and action-oriented.
Your goal is to highlight Lemda’s strengths and guide users toward starting a project.
`,
};

const QUICK_SUGGESTIONS = [
  'Show me your portfolio',
  'What’s your pricing?',
  'How long for a website?',
  'Talk to a human',
];

// Configuration for human-handoff channels
const WHATSAPP_NUMBER = '94770837053';
const SUPPORT_EMAIL   = 'lemda.webdev@gmail.com';

export default function ChatAssistant() {
  const theme    = useTheme();
  const navigate = useNavigate();
  const bottomRef = useRef(null);

  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(false);
  const [input,   setInput]   = useState('');
  const [messages, setMessages] = useState([
    LEMDA_SYSTEM,
    {
      role: 'assistant',
      content:
        '👋 Hello! I’m Lemda Assist, your expert in web, mobile & AI solutions. How may I assist you today?',
    },
  ]);

  // auto-scroll on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // build plain-text history from any array of messages
  const buildHistoryText = (msgs) =>
    msgs
      .filter(m => m.role !== 'system')
      .map(m =>
        m.role === 'user'
          ? `You: ${m.content}`
          : `Lemda: ${m.content}`
      )
      .join('\n');

  // human-handoff: opens appropriate channel with full history
  const handleChannel = (channel, historyMsgs) => {
    const history = encodeURIComponent(buildHistoryText(historyMsgs));

    if (channel === 'WhatsApp') {
      const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${history}`;
      window.open(url, '_blank');
    }
    if (channel === 'Email') {
      const subject = encodeURIComponent('Inquiry from website chat');
      const url = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${history}`;
      window.open(url, '_blank');
    }
    if (channel === 'Call') {
      window.location.href = `tel:+94${WHATSAPP_NUMBER}`;
    }
    if (channel === 'Facebook') {
      window.open('https://web.facebook.com/Lemda.webdevelopment', '_blank');
    }
    setOpen(false);
  };

  // quick intents to show portfolio, pricing, timeline
  const handleLocalIntent = text => {
    const t = text.toLowerCase();
    if (t === 'show me your portfolio') {
      setMessages(m => [
        ...m,
        { role: 'assistant', content: 'Certainly! Please take a look at our featured projects below:' },
        { role: 'assistant', content: '[Explore Our Projects](/projects)' },
      ]);
      return true;
    }
    if (t.includes('pricing')) {
      setMessages(m => [
        ...m,
        { role: 'assistant', content: 'I’d be happy to share our packages—please review here:' },
        { role: 'assistant', content: '[See Pricing & Packages](/services)' },
      ]);
      return true;
    }
    if (t.includes('how long')) {
      setMessages(m => [
        ...m,
        { role: 'assistant', content: 'Typical website builds take 2–4 weeks. For a tailored timeline, reach out:' },
        { role: 'assistant', content: '[Contact Us for Timeline](/contact)' },
      ]);
      return true;
    }
    return false;
  };

  // main send handler
  const sendMessage = async (manualText) => {
    const text = (manualText ?? input).trim();
    if (!text) return;

    // include new message immediately in local array
    const newUserMsg = { role: 'user', content: text };
    const updatedMsgs = [...messages, newUserMsg];
    setMessages(updatedMsgs);
    setInput('');

    // if user selected a human channel directly
    if (['WhatsApp','Email','Call','Facebook'].includes(text)) {
      return handleChannel(text, updatedMsgs);
    }

    // custom local intents
    if (handleLocalIntent(text)) return;

    // otherwise, query OpenAI via your backend
    setLoading(true);
    try {
      const res = await fetch('https://lemda.co.uk/web_api/chat.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: updatedMsgs,
          max_tokens: 120,
          temperature: 0.6,
        }),
      });
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) {
        throw new Error('Invalid JSON from server');
      }
      const data = await res.json();
      if (data.error) throw new Error(data.error);

      setMessages(m => [...m, data.choices[0].message]);
    } catch (err) {
      setMessages(m => [
        ...m,
        { role: 'assistant', content: `❗️ Oops: ${err.message}` },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // detect markdown-style “[Label](/path)” replies
  const mdLinkRE = /^\s*\[([^\]]+)]\(([^)]+)\)\s*$/;
  const parseMarkdownLink = text => {
    const m = text.match(mdLinkRE);
    return m ? { label: m[1], url: m[2] } : null;
  };

  // render link buttons either internal (React Router) or external
  const ActionLinkCard = ({ label, url }) => {
    const handleClick = () => {
      if (url.startsWith('/')) navigate(url);
      else window.open(url, '_blank');
      setOpen(false);
    };
    return (
      <Button
        variant="contained"
        size="small"
        onClick={handleClick}
        sx={{ textTransform: 'none', mb: 1 }}
      >
        {label}
      </Button>
    );
  };

  // fallback UI when AI suggests human handoff
  const HUMAN_PROMPT = 'I might need a human to help.';
  const HumanChoiceCard = () => (
    <Box
      sx={{
        bgcolor: theme.palette.background.paper,
        p: 2,
        borderRadius: 2,
        boxShadow: 1,
        maxWidth: '100%',
      }}
    >
      <Typography variant="body2" sx={{ mb: 1 }}>
        {HUMAN_PROMPT} Which channel would you prefer?
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        {['WhatsApp','Call','Email','Facebook'].map(ch => {
          const Icon = ch === 'WhatsApp'
            ? WhatsAppIcon
            : ch === 'Call'
              ? PhoneIcon
              : ch === 'Email'
                ? EmailIcon
                : FacebookIcon;
          return (
            <Button
              key={ch}
              size="small"
              variant="outlined"
              startIcon={<Icon />}
              onClick={() => handleChannel(ch, messages)}
            >
              {ch}
            </Button>
          );
        })}
      </Stack>
    </Box>
  );

  return (
    <>
      {/* Floating AI button */}
      <Fab
        color="primary"
        aria-label="AI Chat"
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1300,
          '@keyframes pulse': {
            '0%': { boxShadow: `0 0 0 0 ${theme.palette.primary.main}88` },
            '70%': { boxShadow: `0 0 0 10px ${theme.palette.primary.main}00` },
            '100%': { boxShadow: `0 0 0 0 ${theme.palette.primary.main}00` },
          },
          animation: 'pulse 2.4s ease-out infinite',
        }}
      >
        <Badge
          overlap="circular"
          color="secondary"
          badgeContent="AI"
          sx={{ '.MuiBadge-badge': { fontSize: 10, minWidth: 18, height: 18 } }}
        >
          <ChatIcon />
        </Badge>
      </Fab>

      {/* Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: { xs: 300, sm: 380 }, p: 2 } }}
      >
        {/* Header */}
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 1 }}>
            <ChatIcon />
          </Avatar>
          <Typography variant="h6" flexGrow={1}>
            Lemda Assist
          </Typography>
          <IconButton onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Quick suggestions */}
        <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {QUICK_SUGGESTIONS.map(text => (
            <Chip
              key={text}
              label={text}
              size="small"
              onClick={() => setTimeout(() => sendMessage(text), 100)}
            />
          ))}
        </Box>

        {/* Chat history */}
        <Box
          sx={{
            flexGrow: 1,
            maxHeight: '55vh',
            overflowY: 'auto',
            mb: 2,
            pr: 1,
          }}
        >
          <AnimatePresence initial={false}>
            {messages.slice(1).map((m, i) => {
              const link = m.role === 'assistant' && parseMarkdownLink(m.content);
              const isHuman = m.role === 'assistant' && m.content.startsWith(HUMAN_PROMPT);

              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  <Box
                    display="flex"
                    justifyContent={m.role === 'user' ? 'flex-end' : 'flex-start'}
                    mb={1}
                  >
                    {isHuman ? (
                      <HumanChoiceCard />
                    ) : link ? (
                      <ActionLinkCard {...link} />
                    ) : (
                      <Box
                        sx={{
                          bgcolor:
                            m.role === 'user'
                              ? theme.palette.primary.light
                              : theme.palette.background.paper,
                          color:
                            m.role === 'user'
                              ? theme.palette.primary.contrastText
                              : theme.palette.text.primary,
                          p: 1.5,
                          borderRadius: 2,
                          boxShadow: 1,
                          maxWidth: '80%',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        <Typography variant="body2">{m.content}</Typography>
                      </Box>
                    )}
                  </Box>
                </motion.div>
              );
            })}

            {loading && (
              <Box display="flex" justifyContent="flex-start" mb={1}>
                <Skeleton
                  variant="rectangular"
                  width={120}
                  height={28}
                  animation="wave"
                  sx={{ borderRadius: 1 }}
                />
              </Box>
            )}
          </AnimatePresence>
          <div ref={bottomRef} />
        </Box>

        {/* Input bar */}
        <Box display="flex" alignItems="center" gap={1}>
          <TextField
            fullWidth
            size="small"
            placeholder="Describe your project…"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendMessage()}
            InputProps={{
              sx: { bgcolor: theme.palette.background.paper, borderRadius: 1 },
            }}
          />
          <IconButton onClick={() => sendMessage()} color="primary" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : <SendIcon />}
          </IconButton>
        </Box>
      </Drawer>
    </>
  );
}
